import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Compass, Eye, Crown, Globe2, Flag, Rocket } from "lucide-react";
import SEO from "@/components/SEO";
import { METRICS } from "@/data/content";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/371a705d-1e9e-4b80-8780-1f565747a945/images/0ad6762dbbc3a822051f5e6041c055abf52c2e644b20291cc1b84247fe5e4099.png";

const PRINCIPLES = [
  {
    icon: Compass,
    title: "Practitioner-led",
    body: "Every engagement is led by senior operators — not generalist consultants — drawn from the industries and disciplines they advise.",
  },
  {
    icon: Eye,
    title: "Instrumented outcomes",
    body: "We define the hypothesis, the metric and the operating cadence on day one. If we can't measure it, we don't promise it.",
  },
  {
    icon: Crown,
    title: "Sovereign by default",
    body: "Data residency, regulatory posture and in-Kingdom delivery are designed in — never retrofitted at the close of the project.",
  },
  {
    icon: Globe2,
    title: "Regional fluency, global rigor",
    body: "Our teams operate fluently in the business cultures of Saudi Arabia, Oman and Qatar while bringing engineering and security rigor benchmarked globally.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Notion Minds Systems — Technology & Engineering Partner, Riyadh"
        description="Notion Minds Systems is a Saudi-headquartered consultancy of practitioners with 25+ years across banking and IT, delivering secure, sovereign, measurable technology outcomes."
        canonical="https://notionminds.sa/about"
      />
      <section className="relative min-h-[70vh] flex items-end pb-20 pt-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <p className="font-mono-accent text-xs text-[#d4af37] mb-5">[ About ]</p>
          <h1 className="font-display text-5xl lg:text-7xl font-light leading-[1.05] max-w-4xl">
            A consultancy of <span className="text-gradient-gold font-semibold">practitioners.</span>
          </h1>
          <p className="mt-8 text-white/70 text-lg max-w-2xl">
            Notion Minds Systems was founded on a simple conviction: the best advice comes from
            people who have actually shipped the work — at scale, under regulation,
            in production.
          </p>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-24">
          <div className="lg:col-span-5">
            <p className="font-mono-accent text-xs text-[#d4af37] mb-4">Our story</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light leading-tight">
              Built in the region, for the region.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-white/70 leading-relaxed text-lg">
            <p>
              Headquartered in Riyadh, Notion Minds Systems brings together senior
              leaders from global system integrators, regulators and hyperscalers —
              all united by a focus on Saudi Arabia, Oman and Qatar.
            </p>
            <p>
              Our practitioners bring over <span className="text-[#d4af37] font-medium">25+ years of experience</span> across Banking and
              Information Technology, with a cumulative industry experience
              <span className="text-[#d4af37] font-medium"> exceeding 50 years</span>.
            </p>
            <p>
              What sets us apart is our strong industry background and practical
              understanding of business and technology challenges. Having worked
              closely within the industry, we understand the evolving needs of
              organizations and deliver solutions that are aligned with real-world
              operational and strategic requirements.
            </p>
            <p className="text-base text-white/55">
              We're expanding our business development network across the Kingdom —{" "}
              <Link
                to="/careers/business-development-partner-saudi-arabia"
                className="text-[#d4af37] hover:underline"
                data-testid="about-bd-partner-link"
              >
                learn about our Independent Business Development Partner opportunity in Saudi Arabia
              </Link>
              .
            </p>
            <p>
              Our delivery posture is unapologetically opinionated. We prefer
              measurable hypotheses over thick decks, sovereign architectures over
              vendor-locked elegance, and quiet engineering excellence over noise.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 border-y border-white/10">
          {METRICS.map((m, i) => (
            <div
              key={m.label}
              className={`py-10 px-4 ${i !== 0 ? "md:border-l border-white/10" : ""}`}
            >
              <div className={`font-display font-semibold text-[#d4af37] ${m.value.length > 4 ? "text-3xl md:text-4xl" : "text-4xl md:text-5xl"}`}>
                {m.value}
              </div>
              <div className="text-xs text-white/50 mt-3 leading-snug">{m.label}</div>
            </div>
          ))}
        </div>

        {/* PURPOSE & DELIVERY */}
        <div className="mt-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            data-testid="about-purpose-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="group relative bg-[#0d0d12] border border-[#24242a] hover:border-[#d4af37]/50 p-10 lg:p-14 transition-all overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/8 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center justify-between mb-10">
                <div className="w-14 h-14 border border-[#d4af37]/50 flex items-center justify-center">
                  <Flag className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
                </div>
                <span className="font-mono-accent text-[10px] text-white/30">01 / Purpose</span>
              </div>
              <h3 className="font-display text-3xl lg:text-4xl font-light mb-6">Our Purpose</h3>
              <p className="text-white/70 text-lg leading-relaxed border-l-2 border-[#d4af37] pl-6">
                To be a trusted GCC technology and digital transformation partner,
                empowering businesses through innovation, intelligent solutions,
                and future-ready digital services.
              </p>
            </div>
          </motion.div>

          <motion.div
            data-testid="about-delivery-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="group relative bg-[#0d0d12] border border-[#24242a] hover:border-[#d4af37]/50 p-10 lg:p-14 transition-all overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-bl from-[#0055FF]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center justify-between mb-10">
                <div className="w-14 h-14 border border-[#d4af37]/50 flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
                </div>
                <span className="font-mono-accent text-[10px] text-white/30">02 / Delivery</span>
              </div>
              <h3 className="font-display text-3xl lg:text-4xl font-light mb-6">What We Deliver</h3>
              <p className="text-white/70 text-lg leading-relaxed border-l-2 border-[#d4af37] pl-6">
                We help organizations across Saudi Arabia, Oman, Qatar, and the
                GCC achieve sustainable growth through secure, scalable, and
                innovative technology solutions in AI, cybersecurity, cloud,
                enterprise applications, and digital transformation — delivered
                with agility, excellence, and a customer-first approach.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="mt-24">
          <p className="font-mono-accent text-xs text-[#d4af37] mb-4">Principles</p>
          <h2 className="font-display text-4xl lg:text-5xl font-light mb-12 max-w-3xl">
            How we choose to operate.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a1a20] border border-[#1a1a20]">
            {PRINCIPLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="p-12 bg-[#050505]"
                  data-testid={`about-principle-${i}`}
                >
                  <div className="w-12 h-12 border border-[#d4af37]/40 flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl font-light mb-4">{p.title}</h3>
                  <p className="text-white/60 leading-relaxed">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
